import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../utils/api";
import DataTable from "../components/DataTable";
import Modal from "../components/Modal";
import { useAuth } from "../contexts/AuthContext";

const AdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const { user } = useAuth();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isNomineeDocModalOpen, setIsNomineeDocModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [summary, setSummary] = useState({
    total: 0,
    direct: 0,
    referred: 0,
    withNominee: 0,
  });

  const [docImages, setDocImages] = useState([]);
  const [nomineeDocImages, setNomineeDocImages] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get("/admin/clients");
        setClients(res.data.data);
        setFilteredClients(res.data.data);
        calculateSummary(res.data.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchClients();
  }, []);

  // Calculate summary statistics
  const calculateSummary = (clientData) => {
    const summary = {
      total: clientData.length,
      direct: clientData.filter((client) => !client.hasReferrer).length,
      referred: clientData.filter((client) => client.hasReferrer).length,
      withNominee: clientData.filter((client) => client.hasNominee).length,
    };
    setSummary(summary);
  };

  // Filter clients based on active tab
  useEffect(() => {
    let filtered = [...clients];

    switch (activeTab) {
      case "direct":
        filtered = clients.filter((client) => !client.hasReferrer);
        break;
      case "referred":
        filtered = clients.filter((client) => client.hasReferrer);
        break;
      case "withNominee":
        filtered = clients.filter((client) => client.hasNominee);
        break;
      default:
        filtered = clients;
    }

    setFilteredClients(filtered);
  }, [activeTab, clients]);

  // Update the columns for the DataTable
  const columns = [
    {
      key: "profilePicture",
      header: "",
      render: (row) => (
        <img
          src={
            row.profilePicture ? row.profilePicture : "/default.png" // Fallback image if profilePicture is null or undefined
          }
          alt="Profile"
          className="profile-pic smaller"
        />
      ),
    },
    { key: "userId", header: "User ID" },
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "phone", header: "Phone" },
    {
      key: "clientType",
      header: "Client Type",
      render: (row) => (
        <div className="client-type-container">
          {row.hasReferrer ? (
            <span className="client-tag referred-client">
              Referred by {row.referrerName || "Someone"}
            </span>
          ) : (
            <span className="client-tag direct-client">Direct Client</span>
          )}
          {row.hasNominee && (
            <span className="client-tag nominee-added">Has Nominee</span>
          )}
        </div>
      ),
    },
    {
      key: "view",
      header: "View",
      render: (row) => (
        <div className="flex gap-2">
          <button
            className="btn btn-sm btn-outline"
            onClick={() => handleViewDocument(row)}
          >
            Documents
            {/* Show count badge if there are documents */}
            {((row.documentImages && row.documentImages.length > 0) ||
              row.documentImage) && (
              <span className="badge badge-sm ml-1">
                {row.documentImages ? row.documentImages.length : 1}
              </span>
            )}
          </button>
          {row.hasNominee &&
            row.nomineeDocumentImages &&
            row.nomineeDocumentImages.length > 0 && (
              <button
                className="btn btn-sm btn-outline nominee-btn"
                onClick={() => handleViewNomineeDocument(row)}
              >
                Nominee Docs
                <span className="badge badge-sm ml-1">
                  {row.nomineeDocumentImages.length}
                </span>
              </button>
            )}
          <Link
            to={`/admin/clients/${row.userId}/investments`}
            className="btn btn-sm btn-primary"
          >
            Investments
          </Link>
          <Link
            to={`/admin/clients/${row.userId}/transactions-ledger`}
            className="btn btn-sm btn-outline"
          >
            Transactions
          </Link>
          <Link
            to={`/admin/clients/${row.userId}/history`}
            className="btn btn-sm btn-outline"
          >
            History
          </Link>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEditClick(row)}
            className="btn btn-sm btn-outline"
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteClick(row)}
            className="btn btn-sm btn-danger"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const handleViewDocument = (row) => {
    if (!row.documentImages || row.documentImages.length === 0) {
      if (row.documentImage) {
        // Handle legacy single document image
        const url = row.documentImage.startsWith("http")
          ? row.documentImage
          : `${process.env.REACT_APP_API_URL}${row.documentImage}`;
        setDocImages([url]);
      } else {
        setDocImages([]);
      }
    } else {
      // Handle array of document images
      const urls = row.documentImages.map((img) =>
        img.startsWith("http") ? img : `${process.env.REACT_APP_API_URL}${img}`
      );
      setDocImages(urls);
    }
    setSelectedDocIndex(0);
    setIsDocModalOpen(true);
  };

  const handleViewNomineeDocument = (row) => {
    if (row.nomineeDocumentImages && row.nomineeDocumentImages.length > 0) {
      // Handle array of nominee document images
      const urls = row.nomineeDocumentImages.map((img) =>
        img.startsWith("http") ? img : `${process.env.REACT_APP_API_URL}${img}`
      );
      setNomineeDocImages(urls);
      setSelectedDocIndex(0);
      setIsNomineeDocModalOpen(true);
    }
  };

  // Function to navigate between documents
  const changeDocument = (direction, isNominee = false) => {
    const imageArray = isNominee ? nomineeDocImages : docImages;

    if (direction === "next") {
      setSelectedDocIndex((prev) =>
        prev < imageArray.length - 1 ? prev + 1 : prev
      );
    } else {
      setSelectedDocIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    setIsLoading(true);
    try {
      await api.delete(`/admin/clients/${selectedClient.userId}`);

      // Update both the main client list and the filtered list
      const updatedClients = clients.filter(
        (client) => client.userId !== selectedClient.userId
      );
      setClients(updatedClients);

      // Recalculate summary after deleting
      calculateSummary(updatedClients);

      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    // Implement your edit logic here
    setIsLoading(true);
    try {
      // Navigate to edit page
      window.location.href = `/admin/clients/${selectedClient.userId}/edit`;
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      setIsEditModalOpen(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold mb-0">
          Welcome, {user?.name || "Admin"}
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/clients/add" className="btn btn-sm btn-primary">
            Add New Client
          </Link>
          <Link to="/admin/ledger" className="btn btn-sm btn-outline">
            View All Transactions
          </Link>
        </div>
      </div>

      {/* Client Summary Cards */}
      <div className="summary-cards mb-6">
        <div className="summary-card">
          <h3>Total Clients</h3>
          <div className="summary-value">{summary.total}</div>
        </div>
        <div className="summary-card">
          <h3>Direct Clients</h3>
          <div className="summary-value">{summary.direct}</div>
        </div>
        <div className="summary-card">
          <h3>Referred Clients</h3>
          <div className="summary-value">{summary.referred}</div>
        </div>
        <div className="summary-card">
          <h3>Clients with Nominee</h3>
          <div className="summary-value">{summary.withNominee}</div>
        </div>
      </div>

      <div className="card main-content">
        <h2 className="text-xl font-semibold mb-4">Client Management</h2>

        {/* Tabs for filtering */}
        <div className="client-tabs mb-4">
          <button
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabChange("all")}
          >
            All Clients ({summary.total})
          </button>
          <button
            className={`tab-btn ${activeTab === "direct" ? "active" : ""}`}
            onClick={() => handleTabChange("direct")}
          >
            Direct Clients ({summary.direct})
          </button>
          <button
            className={`tab-btn ${activeTab === "referred" ? "active" : ""}`}
            onClick={() => handleTabChange("referred")}
          >
            Referred Clients ({summary.referred})
          </button>
          <button
            className={`tab-btn ${activeTab === "withNominee" ? "active" : ""}`}
            onClick={() => handleTabChange("withNominee")}
          >
            With Nominee ({summary.withNominee})
          </button>
        </div>

        {/* Search and filter controls could go here */}

        <DataTable columns={columns} data={filteredClients} />
      </div>

      {/* View Client Document Modal */}
      <Modal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        title="Client Documents"
        size="md"
      >
        {docImages.length > 0 ? (
          <div className="document-viewer">
            <img
              src={docImages[selectedDocIndex]}
              alt={`Document ${selectedDocIndex + 1}`}
              className="w-full h-auto"
            />

            {/* Document navigation controls */}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm">
                Document {selectedDocIndex + 1} of {docImages.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => changeDocument("prev")}
                  disabled={selectedDocIndex === 0}
                  className="btn btn-sm btn-outline"
                >
                  Previous
                </button>
                <button
                  onClick={() => changeDocument("next")}
                  disabled={selectedDocIndex === docImages.length - 1}
                  className="btn btn-sm btn-outline"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p>No documents available.</p>
        )}
      </Modal>

      {/* View Nominee Document Modal */}
      <Modal
        isOpen={isNomineeDocModalOpen}
        onClose={() => setIsNomineeDocModalOpen(false)}
        title="Nominee Documents"
        size="md"
      >
        {nomineeDocImages.length > 0 ? (
          <div className="document-viewer">
            <img
              src={nomineeDocImages[selectedDocIndex]}
              alt={`Nominee Document ${selectedDocIndex + 1}`}
              className="w-full h-auto"
            />

            {/* Document navigation controls */}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm">
                Document {selectedDocIndex + 1} of {nomineeDocImages.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => changeDocument("prev", true)}
                  disabled={selectedDocIndex === 0}
                  className="btn btn-sm btn-outline"
                >
                  Previous
                </button>
                <button
                  onClick={() => changeDocument("next", true)}
                  disabled={selectedDocIndex === nomineeDocImages.length - 1}
                  className="btn btn-sm btn-outline"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p>No nominee documents available.</p>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        showSpinner={isLoading}
        size="sm"
        buttons={[
          {
            text: "Cancel",
            onClick: () => setIsDeleteModalOpen(false),
            color: "secondary",
          },
          {
            text: "Delete",
            onClick: handleDelete,
            color: "danger",
            disabled: isLoading,
          },
        ]}
      >
        <p>
          Are you sure you want to delete client{" "}
          <strong>{selectedClient?.name}</strong>? This will delete all the
          transaction history of {selectedClient?.name} as well.
          <br />
          <br />
          <strong>NOTE: This action cannot be undone.</strong>
        </p>
      </Modal>

      {/* Deleting / Processing Spinner */}
      <Modal
        isOpen={isLoading}
        showSpinner={true}
        spinnerText="Processing request"
        spinnerSubText="Please wait while we process your request. You will be redirected shortly."
        size="md"
      />

      {/* Edit Confirmation Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Confirm Edit"
        showSpinner={isLoading}
        size="sm"
        buttons={[
          {
            text: "Cancel",
            onClick: () => setIsEditModalOpen(false),
            color: "secondary",
          },
          {
            text: "Continue to Edit",
            onClick: handleEdit,
            color: "primary",
            disabled: isLoading,
          },
        ]}
      >
        <p>
          You are about to edit client <strong>{selectedClient?.name}</strong>.
          Would you like to continue?
          {selectedClient?.hasNominee && (
            <div className="nominee-info mt-2">
              <strong>Note:</strong> This client has a nominee assigned. The
              nominee information will also be available for editing.
            </div>
          )}
          {selectedClient?.hasReferrer && (
            <div className="referrer-info mt-2">
              <strong>Note:</strong> This client was referred by{" "}
              {selectedClient?.referrerName || "someone"}. The referrer
              information will also be available for editing.
            </div>
          )}
        </p>
      </Modal>

      {/* Add this style block at the bottom */}
      <style jsx>{`
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .summary-card {
          padding: 16px;
          border-radius: 8px;
          background-color: #f8f9fa;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .summary-card h3 {
          margin: 0;
          font-size: 14px;
          color: #6c757d;
        }

        .summary-value {
          font-size: 24px;
          font-weight: bold;
          margin-top: 8px;
        }

        .client-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }

        .tab-btn {
          padding: 8px 16px;
          border-radius: 4px;
          background: none;
          border: 1px solid #dee2e6;
          cursor: pointer;
        }

        .tab-btn.active {
          background-color: #e9ecef;
          border-color: #ced4da;
          font-weight: bold;
        }

        .client-type-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .client-tag {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          text-align: center;
        }

        .direct-client {
          background-color: #d4edda;
          color: #155724;
        }

        .referred-client {
          background-color: #cce5ff;
          color: #004085;
        }

        .nominee-added {
          background-color: #fff3cd;
          color: #856404;
        }

        .nominee-btn {
          background-color: #fff3cd;
          border-color: #ffeeba;
        }

        .nominee-info,
        .referrer-info {
          padding: 8px;
          border-radius: 4px;
          font-size: 14px;
        }

        .nominee-info {
          background-color: #fff3cd;
        }

        .referrer-info {
          background-color: #cce5ff;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
